import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Camera, Sparkles, AlertCircle, Plus, Trash2, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, MenuItem } from '../../services/dataService';
import { ExtractedMenuItemRow, menuImageAIService } from '../../services/menuImageAIService';

interface MenuImageImportModalProps {
  onClose: () => void;
  onSuccess: (summary: { created: number; updated: number; totalRows: number; warnings: string[] }) => void;
}

type ImportMode = 'create' | 'update' | 'createAndUpdate';

const getModeForDataService = (mode: ImportMode): ImportMode => {
  // Existing bulkCreateMenuItems has create/update branch semantics inverted.
  if (mode === 'create') return 'update';
  if (mode === 'update') return 'create';
  return 'createAndUpdate';
};

const DEFAULT_PREP_TIME = 10;

type RowImportStatus = {
  label: string;
  tone: 'green' | 'blue' | 'yellow';
};

const MenuImageImportModal: React.FC<MenuImageImportModalProps> = ({ onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ExtractedMenuItemRow[]>([]);
  const [existingMenuItemNames, setExistingMenuItemNames] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loadingExtract, setLoadingExtract] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [mode, setMode] = useState<ImportMode>('createAndUpdate');
  const [showReview, setShowReview] = useState(false);
  const [selectedRowIndexes, setSelectedRowIndexes] = useState<number[]>([]);
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const [bulkSourceCategory, setBulkSourceCategory] = useState('all');

  const invalidRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          !row.name.trim() ||
          !row.category.trim() ||
          !Number.isFinite(Number(row.price)) ||
          Number(row.price) <= 0
      ),
    [rows]
  );
  const existingNameSet = useMemo(
    () => new Set(existingMenuItemNames.map((name) => name.toLowerCase())),
    [existingMenuItemNames]
  );
  const rowStatuses = useMemo(() => {
    const statuses: RowImportStatus[] = [];

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const normalizedName = row.name.trim().toLowerCase();
      const isExisting = normalizedName ? existingNameSet.has(normalizedName) : false;

      if (isExisting) {
        if (mode === 'create') {
          statuses.push({ label: 'Already in menu - will skip', tone: 'yellow' });
        } else {
          statuses.push({ label: 'Already in menu - will update', tone: 'blue' });
        }
      } else if (mode === 'update') {
        statuses.push({ label: 'New item - will skip', tone: 'yellow' });
      } else {
        statuses.push({ label: 'New item', tone: 'green' });
      }
    }

    return statuses;
  }, [rows, existingNameSet, mode]);
  const availableCategories = useMemo(() => {
    return Array.from(
      new Set(rows.map((row) => row.category.trim()).filter((category) => category.length > 0))
    ).sort((a, b) => a.localeCompare(b));
  }, [rows]);
  const allRowsSelected = rows.length > 0 && selectedRowIndexes.length === rows.length;

  const handleFilePick = (file: File | null) => {
    if (!file) return;
    setSelectedFile(file);
    setShowReview(false);
    setRows([]);
    setExistingMenuItemNames([]);
    setWarnings([]);
    setError('');
    setSelectedRowIndexes([]);
    setBulkCategoryValue('');
    setBulkSourceCategory('all');
  };

  const handleExtract = async () => {
    if (!selectedFile || !tenantData?.id) return;

    try {
      setLoadingExtract(true);
      setError('');
      const response = await menuImageAIService.extractMenuFromImage(selectedFile, tenantData.id);
      const menuItems = await dataService.getMenuItems(tenantData.id);
      setRows(response.items);
      setExistingMenuItemNames(menuItems.map((item) => item.name));
      setWarnings(response.warnings || []);
      setShowReview(true);
      setSelectedRowIndexes([]);
      setBulkCategoryValue('');
      setBulkSourceCategory('all');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extract menu from image.');
    } finally {
      setLoadingExtract(false);
    }
  };

  const updateRow = (index: number, updates: Partial<ExtractedMenuItemRow>) => {
    setRows((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, ...updates } : row))
    );
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, idx) => idx !== index));
    setSelectedRowIndexes((prev) =>
      prev.filter((selectedIdx) => selectedIdx !== index).map((selectedIdx) => (
        selectedIdx > index ? selectedIdx - 1 : selectedIdx
      ))
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { category: 'Uncategorized', name: '', price: 0, description: '' },
    ]);
  };

  const toggleRowSelection = (index: number) => {
    setSelectedRowIndexes((prev) =>
      prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
    );
  };

  const toggleSelectAllRows = () => {
    if (allRowsSelected) {
      setSelectedRowIndexes([]);
      return;
    }

    const nextSelection: number[] = [];
    for (let index = 0; index < rows.length; index++) {
      nextSelection.push(index);
    }
    setSelectedRowIndexes(nextSelection);
  };

  const applyBulkCategoryToSelected = () => {
    const nextCategory = bulkCategoryValue.trim();
    if (!nextCategory || selectedRowIndexes.length === 0) return;

    setRows((prev) =>
      prev.map((row, index) => (
        selectedRowIndexes.includes(index) ? { ...row, category: nextCategory } : row
      ))
    );
  };

  const applyBulkCategoryByCurrentCategory = () => {
    const nextCategory = bulkCategoryValue.trim();
    if (!nextCategory || bulkSourceCategory === 'all') return;

    setRows((prev) =>
      prev.map((row) => (
        row.category.trim().toLowerCase() === bulkSourceCategory.toLowerCase()
          ? { ...row, category: nextCategory }
          : row
      ))
    );
  };

  const handleSave = async () => {
    if (!tenantData?.id || rows.length === 0 || invalidRows.length > 0) return;

    try {
      setLoadingSave(true);
      setError('');

      const payload: Partial<MenuItem>[] = rows.map((row) => ({
        ...{
          name: row.name.trim(),
          category: row.category.trim(),
          price: Number(Number(row.price).toFixed(2)),
          preparationTime: DEFAULT_PREP_TIME,
          isAvailable: true,
          autoDeductInventory: false,
          saleUnit: 'piece',
        },
        ...(row.description?.trim() ? { description: row.description.trim() } : {}),
      }));

      const result = await dataService.bulkCreateMenuItems(
        tenantData.id,
        payload,
        getModeForDataService(mode)
      );

      if (!result.success && result.created === 0 && result.updated === 0) {
        setError('Unable to import menu rows. Please review and try again.');
        return;
      }

      const mergedWarnings = [
        ...warnings,
        ...result.errors.map((item) => String(item?.message || 'Unknown import warning')),
      ];

      onSuccess({
        created: result.created,
        updated: result.updated,
        totalRows: result.totalRows,
        warnings: mergedWarnings,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save extracted menu.');
    } finally {
      setLoadingSave(false);
    }
  };

  const content = (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Import Menu From Photo</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Upload a menu image, review AI extraction, then save to your menu.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(92vh-160px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 cursor-pointer hover:border-primary-500">
              <div className="flex items-center gap-3">
                <Upload className="w-5 h-5 text-primary-600" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Upload Image</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">JPG, PNG, WEBP up to 5MB</p>
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
              />
            </label>

            <label className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 cursor-pointer hover:border-primary-500">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-primary-600" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Capture With Camera</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Use phone camera for live capture</p>
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          {selectedFile && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
              <span className="font-medium text-gray-900 dark:text-white">Selected:</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">{selectedFile.name}</span>
            </div>
          )}

          {!showReview && (
            <button
              onClick={handleExtract}
              disabled={!selectedFile || loadingExtract}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loadingExtract ? (
                <span>Extracting...</span>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Extract Menu Using AI
                </>
              )}
            </button>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">Warnings</p>
              <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                {warnings.slice(0, 8).map((warning, index) => (
                  <li key={`${warning}_${index}`}>- {warning}</li>
                ))}
              </ul>
            </div>
          )}

          {showReview && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={addRow} className="btn-secondary text-sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </button>

                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Extracted rows: <span className="font-semibold">{rows.length}</span>
                </div>

                {invalidRows.length > 0 && (
                  <div className="text-sm text-red-600 dark:text-red-400">
                    Fix {invalidRows.length} invalid row(s) before saving.
                  </div>
                )}
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Bulk Category Tools</p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                  <input
                    value={bulkCategoryValue}
                    onChange={(e) => setBulkCategoryValue(e.target.value)}
                    placeholder="Category to apply (e.g. Coffee)"
                    className="input-field text-sm"
                  />
                  <select
                    value={bulkSourceCategory}
                    onChange={(e) => setBulkSourceCategory(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="all">Select existing category to replace</option>
                    {availableCategories.map((category) => (
                      <option key={`bulk_category_${category}`} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    Selected rows: {selectedRowIndexes.length} of {rows.length}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={applyBulkCategoryToSelected}
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={!bulkCategoryValue.trim() || selectedRowIndexes.length === 0}
                  >
                    Apply to Selected Rows
                  </button>
                  <button
                    onClick={applyBulkCategoryByCurrentCategory}
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={!bulkCategoryValue.trim() || bulkSourceCategory === 'all'}
                  >
                    Replace Selected Category
                  </button>
                </div>
              </div>

              <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={allRowsSelected}
                          onChange={toggleSelectAllRows}
                        />
                      </th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-left">Item Name</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Price</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const rowInvalid =
                        !row.name.trim() || !row.category.trim() || Number(row.price) <= 0;
                      const rowStatus = rowStatuses[index];
                      return (
                        <tr key={`row_${index}`} className={rowInvalid ? 'bg-red-50/40 dark:bg-red-900/10' : ''}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedRowIndexes.includes(index)}
                              onChange={() => toggleRowSelection(index)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={row.category}
                              onChange={(e) => updateRow(index, { category: e.target.value })}
                              className="input-field text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={row.name}
                              onChange={(e) => updateRow(index, { name: e.target.value })}
                              className="input-field text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={[
                                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                rowStatus?.tone === 'blue'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                  : rowStatus?.tone === 'yellow'
                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                                    : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
                              ].join(' ')}
                            >
                              {rowStatus?.label || 'New item'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.price}
                              onChange={(e) => updateRow(index, { price: Number(e.target.value) })}
                              className="input-field text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={row.description || ''}
                              onChange={(e) => updateRow(index, { description: e.target.value })}
                              className="input-field text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => removeRow(index)}
                              className="text-red-600 hover:text-red-700"
                              title="Remove row"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Import Behavior</p>
                <div className="space-y-2 text-sm">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="import-mode-photo"
                      value="create"
                      checked={mode === 'create'}
                      onChange={() => setMode('create')}
                      className="mr-2"
                    />
                    Create only (new names only)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="import-mode-photo"
                      value="update"
                      checked={mode === 'update'}
                      onChange={() => setMode('update')}
                      className="mr-2"
                    />
                    Update existing only (matched by item name)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="import-mode-photo"
                      value="createAndUpdate"
                      checked={mode === 'createAndUpdate'}
                      onChange={() => setMode('createAndUpdate')}
                      className="mr-2"
                    />
                    Create and update
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {showReview && (
            <button
              onClick={handleSave}
              disabled={loadingSave || rows.length === 0 || invalidRows.length > 0}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingSave ? (
                'Saving...'
              ) : (
                <span className="inline-flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save To Menu
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default MenuImageImportModal;
